import { useState, useRef } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useRegisterModal } from "@/context/RegisterModalContext";
import { useLanguage } from "@/context/LanguageContext";
import { useLenisPause } from "@/hooks/useLenisPause";
import { PORTAL_URL } from "@/lib/data";
import { AGREEMENT_TEXTS } from "@/lib/agreementText";
import { RequestStep } from "./RequestStep";
import { UnderReviewStep } from "./UnderReviewStep";
import { AgreementStep } from "./AgreementStep";
import { SuccessStep } from "./SuccessStep";

const INITIAL = {
  contactName: "", email: "", phone: "", artistOrLabel: "", accountType: "",
  catalogSize: "", releaseFrequency: "", experience: "", goals: "", socialLinks: "",
};

const LOCALES = { es: "es-ES", en: "en-US", pt: "pt-BR" };

const fillAgreementPlaceholders = (text, labelName, effectiveDateStr) =>
  text.replace(/\{\{LABEL_NAME\}\}/g, labelName || "").replace(/\{\{EFFECTIVE_DATE\}\}/g, effectiveDateStr);

export const RegisterModal = () => {
  const { isOpen, closeModal } = useRegisterModal();
  const { t, language } = useLanguage();
  useLenisPause(isOpen);
  const [step, setStep] = useState("request");
  const [form, setForm] = useState(INITIAL);
  const [accountTypeError, setAccountTypeError] = useState(false);
  const [releaseFrequencyError, setReleaseFrequencyError] = useState(false);
  const [requestSending, setRequestSending] = useState(false);
  const [requestError, setRequestError] = useState(false);
  const [requestRateLimited, setRequestRateLimited] = useState(false);
  const [requestInfo, setRequestInfo] = useState(null);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [sending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);
  const scrollRef = useRef(null);

  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const resetAndClose = () => {
    setStep("request");
    setForm(INITIAL);
    setAccountTypeError(false);
    setReleaseFrequencyError(false);
    setRequestSending(false);
    setRequestError(false);
    setRequestRateLimited(false);
    setRequestInfo(null);
    setAgreed(false);
    setScrolledToEnd(false);
    setSending(false);
    setSubmitError(false);
    setRateLimited(false);
    closeModal();
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    if (!form.accountType) {
      setAccountTypeError(true);
      return;
    }
    if (!form.releaseFrequency) {
      setReleaseFrequencyError(true);
      return;
    }
    if (!form.contactName || !form.email || !form.phone || !form.artistOrLabel || !form.catalogSize ||
        !form.experience || !form.goals || !form.socialLinks) return;
    if (requestSending) return;

    setRequestSending(true);
    setRequestError(false);
    setRequestRateLimited(false);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/distribution-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, language }),
      });
      if (!res.ok) {
        if (res.status === 429) setRequestRateLimited(true);
        throw new Error(`distribution-requests failed with status ${res.status}`);
      }
      const data = await res.json();
      setRequestInfo({ code: data.code, submittedAt: data.submitted_at });
      setStep("under-review");
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.error("No se pudo enviar la solicitud:", err);
      setRequestError(true);
    } finally {
      setRequestSending(false);
    }
  };

  const handleAgreementScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
      setScrolledToEnd(true);
    }
  };

  const handleAccept = async () => {
    if (!agreed || sending) return;
    setSending(true);
    setSubmitError(false);
    setRateLimited(false);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.contactName,
          email: form.email,
          label: form.artistOrLabel,
          accountType: form.accountType,
          language,
          acceptedAgreement: true,
          requestCode: requestInfo?.code || null,
        }),
      });
      if (!res.ok) {
        if (res.status === 429) setRateLimited(true);
        throw new Error(`register failed with status ${res.status}`);
      }
      setStep("success");
      setTimeout(() => {
        window.open(PORTAL_URL, "_blank", "noopener,noreferrer");
        resetAndClose();
      }, 3200);
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.error("No se pudo procesar el registro:", err);
      setSubmitError(true);
      setSending(false);
    }
  };

  const effectiveDateStr = new Date().toLocaleDateString(LOCALES[language] || LOCALES.es);
  const agreementBody = fillAgreementPlaceholders(AGREEMENT_TEXTS[language] || AGREEMENT_TEXTS.es, form.artistOrLabel, effectiveDateStr);
  const submittedAtStr = requestInfo?.submittedAt
    ? new Date(requestInfo.submittedAt).toLocaleString(LOCALES[language] || LOCALES.es)
    : "";

  const modalWidth = step === "request" ? "sm:max-w-[560px]" : step === "agreement" ? "sm:max-w-[560px]" : "sm:max-w-[440px]";

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && resetAndClose()}>
      <DialogContent
        data-testid="register-modal"
        className={`bg-[#0F0F10] border border-white/10 text-white ${modalWidth} rounded-2xl p-0 overflow-hidden gap-0`}
      >
        {step === "success" && <SuccessStep t={t} code={requestInfo?.code} />}

        {step === "request" && (
          <RequestStep
            t={t}
            form={form}
            onChange={handleChange}
            accountTypeError={accountTypeError}
            onAccountTypeChange={(v) => { setForm((f) => ({ ...f, accountType: v })); setAccountTypeError(false); }}
            releaseFrequencyError={releaseFrequencyError}
            onReleaseFrequencyChange={(v) => { setForm((f) => ({ ...f, releaseFrequency: v })); setReleaseFrequencyError(false); }}
            requestSending={requestSending}
            requestError={requestError}
            requestRateLimited={requestRateLimited}
            onSubmit={handleRequestSubmit}
          />
        )}

        {step === "under-review" && (
          <UnderReviewStep t={t} code={requestInfo?.code} submittedAtStr={submittedAtStr} onContinue={() => setStep("agreement")} />
        )}

        {step === "agreement" && (
          <AgreementStep
            t={t}
            scrollRef={scrollRef}
            onScroll={handleAgreementScroll}
            agreementBody={agreementBody}
            scrolledToEnd={scrolledToEnd}
            agreed={agreed}
            onAgreedChange={setAgreed}
            submitError={submitError}
            rateLimited={rateLimited}
            sending={sending}
            onAccept={handleAccept}
            onBack={() => setStep("under-review")}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
